import {
  AlertOutlined,
  ArrowRightOutlined,
  AuditOutlined,
  CarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  FundOutlined,
  WarningOutlined
} from '@ant-design/icons';
import type { ApplyRow } from '@hq/api-sdk';
import { ApiError, CachePolicy } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { formatAmount, formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Card, Col, Progress, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { canViewAmount, MoneyText } from '../components/Money';
import { SlaBadge } from '../components/SlaBadge';
import { ApplyStatusTag } from '../components/StatusTag';
import { readOrgBrand } from '../org-brand';
import { useAuthStore } from '../stores/auth';

/**
 * 工作台 = 待办驾驶舱（docs/01 §6.2-1）：
 * 待办、待签收、库存预警、预算进度、异常提醒五要素 + 点击直达。
 * 每张卡按权限码决定是否请求，避免无权限账号进页面就刷 403（fail-closed）。
 */
export default function Workbench() {
  const navigate = useNavigate();
  const profile = useAuthStore((state) => state.profile);
  const brand = readOrgBrand();
  const orgName = brand?.orgShortName || brand?.orgName || null;

  const can = (perm: string): boolean =>
    profile?.level === 'admin' || (profile?.perms ? hasPermission(profile.perms, perm) : false);

  const canApprove = can('wl:apply:approve');
  const canSign = can('wl:delivery:sign');
  const canInventory = can('wl:inventory:manage');
  const canAnalytics = can('wl:analytics:view');

  const todo = useQuery({
    queryKey: ['workbench', 'todo'],
    enabled: canApprove,
    staleTime: CachePolicy.aggregate,
    placeholderData: keepPreviousData,
    queryFn: () => hq.approval.todo({ size: 50 })
  });

  const signable = useQuery({
    queryKey: ['workbench', 'signable'],
    enabled: canSign,
    staleTime: CachePolicy.aggregate,
    placeholderData: keepPreviousData,
    queryFn: () => hq.delivery.listSignable({ size: 20 })
  });

  const alerts = useQuery({
    queryKey: ['workbench', 'alerts'],
    enabled: canInventory,
    staleTime: CachePolicy.aggregate,
    placeholderData: keepPreviousData,
    queryFn: () => hq.stockAlert.summary()
  });

  // 预算执行：读本院预算余额（/api/wl/budget/balance，机构口径）。
  // 不能用 /api/gov/*：该前缀由门户门禁限 GOV/SYSTEM，HOSPITAL 门户调用一律 403。
  const budget = useQuery({
    queryKey: ['workbench', 'budget'],
    staleTime: CachePolicy.aggregate,
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.budget.balance({})
  });

  // 异常与准时率：读本院监管快照（/api/wl/analytics/gov/summary，非跨机构账号只返回本院一行）
  const summary = useQuery({
    queryKey: ['workbench', 'gov-summary'],
    enabled: canAnalytics,
    staleTime: CachePolicy.aggregate,
    placeholderData: keepPreviousData,
    queryFn: () => hq.analytics.govSummary({})
  });

  const recent = useQuery({
    queryKey: ['workbench', 'recent'],
    staleTime: CachePolicy.aggregate,
    placeholderData: keepPreviousData,
    queryFn: () => hq.apply.list({ size: 5 })
  });

  const error = [todo, signable, alerts, budget, summary, recent].find((item) => item.error)?.error;
  const errorMessage = error instanceof ApiError ? error.friendlyMessage : null;

  const rows = recent.data?.items ?? [];
  const draftCount = rows.filter((row) => row.bizStatus === 'DRAFT').length;
  const totalAmount = rows.reduce((sum, row) => sum + row.totalAmount, 0);

  const budgetBalance = budget.data;
  const budgetPercent = Math.round((budgetBalance?.execRate ?? 0) * 100);

  const columns: ColumnsType<ApplyRow> = [
    {
      title: '单号',
      dataIndex: 'bizNo',
      width: 200,
      render: (value: string, row) => (
        <Typography.Link onClick={() => navigate(`/apply/${row.id}`)}>{value}</Typography.Link>
      )
    },
    { title: '标题', dataIndex: 'applyTitle', ellipsis: true, render: (value: string | null) => value || '-' },
    {
      title: '状态',
      dataIndex: 'bizStatus',
      width: 110,
      render: (value: ApplyRow['bizStatus']) => <ApplyStatusTag status={value} />
    },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => <MoneyText value={value} /> },
    {
      title: '时效',
      dataIndex: 'slaDeadline',
      width: 150,
      render: (value: string | null, row) => (
        <SlaBadge
          deadline={value}
          finished={['SIGNED', 'CLOSED', 'CANCELED', 'REJECTED'].includes(row.bizStatus)}
          finishedText={row.bizStatus === 'SIGNED' ? '已签收' : undefined}
        />
      )
    },
    {
      title: '申请时间',
      dataIndex: 'bizDate',
      width: 150,
      render: (value: string) => formatDisplayTime(value)
    }
  ];

  return (
    <>
      {errorMessage ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="部分待办数据加载失败"
          description={errorMessage}
        />
      ) : null}

      <Card className="page-card">
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          欢迎，{profile?.name ?? '用户'}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          当前机构 {orgName ?? '-'}；下表为你的待办与关键指标，点击卡片直达处理页面。
        </Typography.Paragraph>
      </Card>

      <Row gutter={[16, 16]} className="page-card">
        <Col xs={24} sm={12} xl={6}>
          <Card hoverable onClick={() => navigate('/approval/todo')} data-testid="workbench-todo">
            <Statistic
              title="待我审批"
              value={todo.data?.items.length ?? 0}
              suffix={todo.data?.hasMore ? '+ 项' : '项'}
              prefix={<AuditOutlined />}
              loading={canApprove && todo.isPending}
              valueStyle={{ color: canApprove && (todo.data?.items.length ?? 0) > 0 ? '#cf1322' : undefined }}
            />
            <Typography.Text type="secondary">
              {canApprove ? '含申领与调价审批' : '无 wl:apply:approve 权限'}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card hoverable onClick={() => navigate('/delivery')} data-testid="workbench-signable">
            <Statistic
              title="待签收配送单"
              value={signable.data?.items.length ?? 0}
              suffix="单"
              prefix={<CarOutlined />}
              loading={canSign && signable.isPending}
              valueStyle={{ color: (signable.data?.items.length ?? 0) > 0 ? '#d46b08' : undefined }}
            />
            <Typography.Text type="secondary">{canSign ? '在途 + 部分签收待补签' : '无 wl:delivery:sign 权限'}</Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card hoverable onClick={() => navigate('/inventory/alert')} data-testid="workbench-alert">
            <Statistic
              title="库存预警"
              value={alerts.data?.total ?? 0}
              suffix="项"
              prefix={<WarningOutlined />}
              loading={canInventory && alerts.isPending}
              valueStyle={{ color: (alerts.data?.total ?? 0) > 0 ? '#cf1322' : undefined }}
            />
            <Typography.Text type="secondary">
              {canInventory
                ? `严重 ${alerts.data?.byLevel?.HIGH ?? alerts.data?.byLevel?.CRITICAL ?? 0} / 效期 ${(alerts.data?.byType?.NEAR_EXPIRY ?? 0) + (alerts.data?.byType?.EXPIRED ?? 0)} / 低库存 ${(alerts.data?.byType?.LOW_STOCK ?? 0) + (alerts.data?.byType?.STOCK_OUT ?? 0)}`
                : '无 wl:inventory:manage 权限'}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card hoverable onClick={() => navigate('/budget')} data-testid="workbench-budget">
            <Statistic
              title="预算执行率"
              value={budgetPercent}
              suffix="%"
              prefix={<FundOutlined />}
              loading={budget.isPending}
              valueStyle={{ color: budgetPercent >= 90 ? '#cf1322' : undefined }}
            />
            <Progress percent={budgetPercent} size="small" showInfo={false} status={budgetPercent >= 90 ? 'exception' : 'active'} />
            <Typography.Text type="secondary">
              {canViewAmount(profile)
                ? budgetBalance && budgetBalance.budgetAmount > 0
                  ? `已执行 ${formatAmount(budgetBalance.usedAmount)} / ${formatAmount(budgetBalance.budgetAmount)}`
                  : '本年度尚未编制/批复预算'
                : '金额按账号权限隐藏'}
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="page-card">
        <Col xs={24} xl={16}>
          <Card
            title="近期申领"
            extra={
              <Typography.Link onClick={() => navigate('/apply')}>
                全部申领 <ArrowRightOutlined />
              </Typography.Link>
            }
          >
            <Table<ApplyRow>
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={rows}
              loading={recent.isPending}
              pagination={false}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: '暂无申领单' }}
              onRow={(row) => ({ onDoubleClick: () => navigate(`/apply/${row.id}`) })}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card title="异常与提醒" extra={<AlertOutlined />}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography.Text type="secondary">未闭环异常</Typography.Text>
                <Typography.Text strong>
                  {canAnalytics ? `${summary.data?.anomalyCount ?? 0} 项` : '—'}
                </Typography.Text>
              </Space>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography.Text type="secondary">异常闭环率</Typography.Text>
                <Typography.Text strong>
                  {canAnalytics ? `${((summary.data?.resolveRate ?? 0) * 100).toFixed(1)}%` : '—'}
                </Typography.Text>
              </Space>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography.Text type="secondary">配送准时率</Typography.Text>
                <Typography.Text strong>
                  {canAnalytics ? `${((summary.data?.deliveryOntimeRate ?? 0) * 100).toFixed(1)}%` : '—'}
                </Typography.Text>
              </Space>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography.Text type="secondary">草稿待提交</Typography.Text>
                <Typography.Text strong>{draftCount} 张</Typography.Text>
              </Space>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography.Text type="secondary">近期金额合计</Typography.Text>
                <Typography.Text strong>
                  <MoneyText value={totalAmount} />
                </Typography.Text>
              </Space>
              <Space wrap>
                <Tag icon={<FileTextOutlined />} color="blue" style={{ cursor: 'pointer' }} onClick={() => navigate('/apply/create')}>
                  新建申领
                </Tag>
                {canInventory ? (
                  <Tag icon={<ClockCircleOutlined />} style={{ cursor: 'pointer' }} onClick={() => navigate('/stocktake')}>
                    盘点
                  </Tag>
                ) : null}
                <Tag icon={<CarOutlined />} style={{ cursor: 'pointer' }} onClick={() => navigate('/delivery/track')}>
                  配送轨迹
                </Tag>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>
    </>
  );
}
